import React, { useCallback } from "react";
import { AlertCircle, File } from "lucide-react";
import { useTargeting, type TargetingArea } from "./TargetingContext";
import { AreaCard } from "./AreaCard";
import { H2hAreaCard } from "./H2hAreaCard";

const HELPER = "Choose areas from the recommended options or create new ones on the map.";

const formatCount = (n: number) => `${n} distribution ${n === 1 ? "area" : "areas"} selected`;

// The "Distribution areas" block below the map (AC20): helper + live count +
// the list of selected-area cards. Appears once a city is selected and its
// recommendations have loaded. Removal is persisted (F4/F5 own the selection
// API), so the cards carry a remove control.
export const DistributionAreasSection = () => {
  const { channel, selectedCity, isAreasLoading, selectedAreas, removeArea, limitError } = useTargeting();

  const handleRemove = useCallback((area: TargetingArea) => removeArea(area.id), [removeArea]);

  if (!selectedCity || isAreasLoading) return null;

  // D2D areas are named generically "Area 1", "Area 2"… in selection order
  // (AC22); H2H keeps each point's real location name (AC21).
  const labelFor = (index: number) => (channel === "D2D" ? `Area ${index + 1}` : undefined);

  // D2D shows the auto-derived coverage inline as "📄 N flyers" (AC22); H2H uses
  // the card's default unit presentation.
  const metaFor = (area: TargetingArea) =>
    channel === "D2D" ? (
      <span className="flex shrink-0 items-center gap-1 text-sm text-muted-foreground">
        <File className="size-4" />
        {(area.units ?? 0).toLocaleString()} flyers
      </span>
    ) : undefined;

  return (
    <div className="flex flex-col gap-3" data-cypress-id="targeting-distribution-areas">
      <div className="flex flex-col gap-1">
        <p className="text-sm text-muted-foreground">{HELPER}</p>
        <p className="text-sm font-medium text-foreground" data-cypress-id="targeting-area-count">
          {formatCount(selectedAreas.length)}
        </p>
      </div>
      {limitError && (
        <div
          role="alert"
          data-cypress-id="targeting-limit-error"
          className="flex items-start gap-2 rounded-md border border-solid border-destructive/30 bg-destructive/5 px-3 py-2.5"
        >
          <AlertCircle className="mt-0.5 size-4 shrink-0 text-destructive" />
          <div className="flex flex-col gap-0.5 text-sm">
            <span className="font-semibold text-destructive">{limitError.title}</span>
            <span className="text-destructive/90">{limitError.message}</span>
          </div>
        </div>
      )}
      {selectedAreas.length > 0 && (
        <div className="flex flex-col gap-2">
          {selectedAreas.map((area, index) =>
            channel === "H2H" ? (
              // H2H cards carry per-area scheduling + their own confirmed removal (F6).
              <H2hAreaCard key={area.id} area={area} />
            ) : (
              <AreaCard
                key={area.id}
                area={area}
                onRemove={handleRemove}
                displayName={labelFor(index)}
                meta={metaFor(area)}
              />
            )
          )}
        </div>
      )}
    </div>
  );
};
